import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, LogOut, FileText, ArrowUpRight, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STAGE_NAMES = [
  "Exploración inicial",
  "Delimitación del problema",
  "Pregunta y objetivos",
  "Justificación",
  "Marco teórico",
  "Diseño metodológico",
  "Instrumentos",
  "Análisis de datos",
  "Redacción del informe",
  "Revisión final",
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ title: "", topic: "", career: user?.career || "", work_type: "", context: "" });
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(false);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProjects(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setErr("");
    try {
      const { data } = await api.post("/projects", form);
      setShowNew(false);
      setForm({ title: "", topic: "", career: user?.career || "", work_type: "", context: "" });
      navigate(`/project/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar este proyecto y su conversación? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid="dashboard-page">
      <Header user={user} onLogout={logout} />

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Hero */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
          <div className="md:col-span-8 fade-slide-up">
            <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent-dark mb-3">
              Panel de proyectos
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-primary-800 leading-[1.05] tracking-tight mb-4">
              Hola, <em className="not-italic">{user?.name?.split(" ")[0] || "investigador/a"}</em>.
              <br />Tu investigación comienza aquí.
            </h1>
            <p className="text-neutral-800 max-w-2xl leading-relaxed">
              Selecciona un proyecto existente para continuar con tu asesora metodológica o
              inicia uno nuevo para estructurar tu trabajo desde la idea inicial.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end items-start">
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-2 bg-primary-800 text-bone px-6 py-3 rounded-sm hover:bg-primary-700 transition font-sans font-medium"
              data-testid="new-project-button"
            >
              <Plus size={16} strokeWidth={1.8} /> Nuevo proyecto
            </button>
          </div>
        </div>

        {/* List */}
        <div className="mb-5 flex items-baseline justify-between border-b border-border pb-3">
          <h2 className="font-serif text-2xl text-primary-800">Tus proyectos</h2>
          <span className="font-mono text-xs tracking-widest text-neutral-600 uppercase">
            {loading ? "Cargando…" : `${projects.length} ${projects.length === 1 ? "proyecto" : "proyectos"}`}
          </span>
        </div>

        {err && (
          <div className="mb-4 px-4 py-3 border border-destructive/30 bg-destructive/5 text-destructive text-sm rounded-sm">
            {err}
          </div>
        )}

        {!loading && projects.length === 0 && (
          <EmptyState onCreate={() => setShowNew(true)} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="projects-grid">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onDelete={() => onDelete(p.id)} />
          ))}
        </div>
      </main>

      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Nuevo proyecto de investigación">
          <form onSubmit={onCreate} className="space-y-4" data-testid="new-project-form">
            <Field label="Título del trabajo">
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                placeholder="Ej. Estrategias de motivación en fútbol formativo"
                data-testid="new-title-input"
              />
            </Field>
            <Field label="Tema preliminar">
              <textarea
                rows={2}
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                placeholder="Describe brevemente el fenómeno que te interesa."
                data-testid="new-topic-input"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Carrera">
                <select
                  value={form.career}
                  onChange={(e) => setForm({ ...form, career: e.target.value })}
                  className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                  data-testid="new-career-select"
                >
                  <option value="">Selecciona</option>
                  <option>Entrenador de Fútbol</option>
                  <option>Técnico de Fútbol</option>
                  <option>Analítica Deportiva</option>
                  <option>Gestión Deportiva</option>
                  <option>Preparación Física</option>
                  <option>Otra</option>
                </select>
              </Field>
              <Field label="Tipo de trabajo">
                <select
                  value={form.work_type}
                  onChange={(e) => setForm({ ...form, work_type: e.target.value })}
                  className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                  data-testid="new-worktype-select"
                >
                  <option value="">Selecciona</option>
                  <option>Trabajo de titulación</option>
                  <option>Seminario de investigación</option>
                  <option>Tesina</option>
                  <option>Proyecto de asignatura</option>
                  <option>Otro</option>
                </select>
              </Field>
            </div>
            <Field label="Contexto (opcional)">
              <input
                value={form.context}
                onChange={(e) => setForm({ ...form, context: e.target.value })}
                className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                placeholder="Ej. Club formativo Sub-15, Región Metropolitana"
                data-testid="new-context-input"
              />
            </Field>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowNew(false)}
                className="px-5 py-2.5 border border-border rounded-sm font-sans hover:bg-neutral-100"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-6 py-2.5 bg-primary-800 text-bone rounded-sm hover:bg-primary-700 disabled:opacity-60 font-sans font-medium"
                data-testid="new-project-submit"
              >
                {creating ? "Creando…" : "Crear e iniciar"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Header({ user, onLogout }) {
  return (
    <header className="border-b border-border bg-white/70 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-3" data-testid="header-brand">
          <img
            src="/inaf-logo.png"
            alt="INAF — Instituto Nacional del Fútbol"
            className="h-9 w-auto"
          />
          <div className="h-8 w-px bg-border" aria-hidden />
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-600 hidden sm:block">
            Asistente<br/>Metodológico
          </div>
        </Link>
        <div className="flex items-center gap-4">
          <div className="text-right leading-tight hidden sm:block">
            <div className="font-sans text-sm text-primary-800">{user?.name}</div>
            <div className="font-mono text-[10px] tracking-wider uppercase text-neutral-600">
              {user?.career || user?.role}
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-primary-800 transition"
            data-testid="logout-button"
          >
            <LogOut size={16} strokeWidth={1.6} /> Salir
          </button>
        </div>
      </div>
    </header>
  );
}

function ProjectCard({ project, onDelete }) {
  const currentStage = Number(project.current_stage) || 1;
  return (
    <div
      className="group relative bg-white border border-border rounded-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-accent-dark">
          Etapa {currentStage} / 10
        </div>
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-destructive"
          title="Eliminar proyecto"
          data-testid={`delete-project-${project.id}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <Link to={`/project/${project.id}`} data-testid={`open-project-${project.id}`}>
        <h3 className="font-serif text-xl text-primary-800 leading-snug mb-2 line-clamp-2">
          {project.title}
        </h3>
        {project.topic && (
          <p className="text-sm text-neutral-800 line-clamp-3 mb-4 leading-relaxed">
            {project.topic}
          </p>
        )}

        {/* stage progress bar: 10 segments */}
        <div className="flex items-center gap-1 mb-4" aria-label="Progreso de etapas">
          {Array.from({ length: 10 }).map((_, i) => (
            <span
              key={i}
              className={`h-1 flex-1 ${i < currentStage ? "bg-accent" : "bg-neutral-200"} transition-colors`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-neutral-600 font-sans">
          <span className="flex items-center gap-1.5">
            <FileText size={12} strokeWidth={1.6} />
            {STAGE_NAMES[currentStage - 1]}
          </span>
          <span className="inline-flex items-center gap-1 text-primary-800 group-hover:text-accent-dark transition">
            Abrir <ArrowUpRight size={14} strokeWidth={1.6} />
          </span>
        </div>
      </Link>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      className="relative border border-border bg-white rounded-sm p-12 text-center tactical-grid"
      data-testid="empty-state"
    >
      <div className="max-w-md mx-auto">
        <img
          src="/inaf-logo.png"
          alt="INAF"
          className="h-14 w-auto mx-auto mb-6"
        />
        <h3 className="font-serif text-2xl text-primary-800 mb-2">
          Aún no tienes proyectos
        </h3>
        <p className="text-neutral-800 mb-6">
          Crea tu primer proyecto de investigación para comenzar a trabajar con tu asesora metodológica.
        </p>
        <button
          onClick={onCreate}
          className="inline-flex items-center gap-2 bg-primary-800 text-bone px-6 py-2.5 rounded-sm hover:bg-primary-700 font-sans font-medium"
          data-testid="empty-new-project-button"
        >
          <Plus size={16} /> Crear proyecto
        </button>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-primary-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="modal-overlay"
    >
      <div
        className="bg-bone border border-border max-w-xl w-full p-8 rounded-sm fade-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent-dark mb-2">
          INAF
        </div>
        <h3 className="font-serif text-2xl text-primary-800 mb-5">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-600 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
